import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import type { TelemetryUploadRequest, TelemetryDataFilter } from "@/types";

export const isTelemetryNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return false;
  }

  const responseError = error as { response?: { status?: number } };
  return responseError.response?.status === 404;
};

// Reusable retry logic: don't retry on 404 (no telemetry data available)
const noRetryOn404 = (failureCount: number, error: unknown): boolean => {
  if (isTelemetryNotFoundError(error)) {
    return false;
  }
  return failureCount < 3;
};

// Query keys
const telemetryKeys = {
  all: ["telemetry"] as const,
  session: (sessionId: string) =>
    [...telemetryKeys.all, "session", sessionId] as const,
  metadata: (sessionId: string) =>
    [...telemetryKeys.session(sessionId), "metadata"] as const,
  data: (sessionId: string, filter?: TelemetryDataFilter) =>
    [...telemetryKeys.session(sessionId), "data", filter] as const,
  summary: (sessionId: string) =>
    [...telemetryKeys.session(sessionId), "summary"] as const,
  laps: (sessionId: string) =>
    [...telemetryKeys.session(sessionId), "laps"] as const,
};

// Hook to get telemetry metadata for a session
export function useTelemetryMetadata(sessionId: string, enabled = true) {
  return useQuery({
    queryKey: telemetryKeys.metadata(sessionId),
    queryFn: () => apiClient.getTelemetryMetadata(sessionId),
    enabled: enabled && !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: noRetryOn404,
  });
}

// Hook to get telemetry data with filtering
export function useTelemetryData(
  sessionId: string,
  filter?: TelemetryDataFilter,
  enabled = true,
) {
  return useQuery({
    queryKey: telemetryKeys.data(sessionId, filter),
    queryFn: () => apiClient.getTelemetryData(sessionId, filter),
    enabled: enabled && !!sessionId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    // Shorter garbage collection time for telemetry data to prevent memory buildup
    // Large telemetry datasets should be cleaned up more aggressively
    gcTime: 5 * 60 * 1000, // 5 minutes (shorter than default)
    retry: noRetryOn404,
  });
}

// Hook to get telemetry lap data
export function useTelemetryLaps(sessionId: string, enabled = true) {
  return useQuery({
    queryKey: telemetryKeys.laps(sessionId),
    queryFn: () => apiClient.getTelemetryLaps(sessionId),
    enabled: enabled && !!sessionId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes - shorter for telemetry data
    retry: noRetryOn404,
  });
}

// Hook to get telemetry summary
export function useTelemetrySummary(sessionId: string, enabled = true) {
  return useQuery({
    queryKey: telemetryKeys.summary(sessionId),
    queryFn: () => apiClient.getTelemetrySummary(sessionId),
    enabled: enabled && !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: noRetryOn404,
  });
}

// Hook to upload telemetry data
function useTelemetryUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      data,
    }: {
      sessionId: string;
      data: TelemetryUploadRequest;
    }) => apiClient.uploadTelemetryData(sessionId, data),
    onSuccess: (_, variables) => {
      // Invalidate all telemetry queries for this session
      queryClient.invalidateQueries({
        queryKey: telemetryKeys.session(variables.sessionId),
      });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

// Hook to delete telemetry data
function useTelemetryDelete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => apiClient.deleteTelemetryData(sessionId),
    onSuccess: (_, sessionId) => {
      // Remove all telemetry queries for this session from cache
      queryClient.removeQueries({
        queryKey: telemetryKeys.session(sessionId),
      });
    },
  });
}

// Combined hook for telemetry management
export function useTelemetryManager(sessionId: string) {
  const metadata = useTelemetryMetadata(sessionId);
  const summary = useTelemetrySummary(sessionId);
  const upload = useTelemetryUpload();
  const deleteTelemetry = useTelemetryDelete();

  const hasTelemetryData = Boolean(metadata.data) && !metadata.error;
  const isTelemetryMissing =
    isTelemetryNotFoundError(metadata.error) ||
    isTelemetryNotFoundError(summary.error);
  const isLoading = metadata.isLoading || summary.isLoading;
  const error = metadata.error || summary.error;

  return {
    // Data
    metadata: metadata.data,
    summary: summary.data,
    hasTelemetryData,
    isTelemetryMissing,

    // Loading states
    isLoading,
    isMetadataLoading: metadata.isLoading,
    isSummaryLoading: summary.isLoading,

    // Error states
    error,
    metadataError: metadata.error,
    summaryError: summary.error,

    // Actions
    upload: {
      mutate: upload.mutate,
      mutateAsync: upload.mutateAsync,
      isLoading: upload.isPending,
      error: upload.error,
      isSuccess: upload.isSuccess,
    },
    delete: {
      mutate: deleteTelemetry.mutate,
      mutateAsync: deleteTelemetry.mutateAsync,
      isLoading: deleteTelemetry.isPending,
      error: deleteTelemetry.error,
      isSuccess: deleteTelemetry.isSuccess,
    },

    // Refresh functions
    refetchMetadata: metadata.refetch,
    refetchSummary: summary.refetch,
  };
}

/** Row budget for telemetry chart fetches (base overview + viewport windows). */
export const TELEMETRY_CHART_MAX_POINTS = 10000;

// Hook that fetches the (optionally channel-scoped) telemetry series once
// and provides cached access. Scoping matters: the backend divides the
// maxPoints budget across the channels in the response, so an all-channel
// request collapses to ~54 buckets on a 45-channel session.
function useTelemetryDataCache(
  sessionId: string,
  channels: string[] | undefined,
  timeRange?: { start: number; end: number },
  maxPoints = TELEMETRY_CHART_MAX_POINTS,
) {
  const masterFilter: TelemetryDataFilter = {
    channels: channels && channels.length > 0 ? channels : undefined,
    startTime: timeRange?.start,
    endTime: timeRange?.end,
    maxPoints,
  };

  return useTelemetryData(sessionId, masterFilter, !!sessionId);
}

// Chart-facing hook: fetches only the selected channels so the decimation
// budget is spent on the plotted series.
export function useTelemetryVisualization(
  sessionId: string,
  selectedChannels: string[],
  timeRange?: { start: number; end: number },
  maxPoints = TELEMETRY_CHART_MAX_POINTS,
) {
  const allDataQuery = useTelemetryDataCache(
    sessionId,
    selectedChannels,
    timeRange,
    maxPoints,
  );

  // Simply return the full dataset - individual charts will extract what they need
  // This eliminates re-processing when channels change
  return {
    allData: allDataQuery.data?.result?.data || [],
    allMetadata: allDataQuery.data?.result?.metadata || {},
    isLoading: allDataQuery.isLoading,
    error: allDataQuery.error,
    isSuccess: allDataQuery.isSuccess,
    refetch: allDataQuery.refetch,
  };
}
